import type { Navigation, Page } from "@sveltejs/kit";
import { BehaviorSubject, Observable, Subscriber, firstValueFrom, lastValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";
import { page, navigating } from "$app/stores";
import type { Readable, Writable } from "svelte/store";

/**
 * Convert a Svelte store to an observable.
 * @param store A Svelte store.
 * @returns An observable.
 */
export function fromStore<T>(store: Readable<T>) {
  return new Observable<T>((observer) => {
    const unsubscribeFromStore = store.subscribe((value) => observer.next(value));

    // Teardown logic, i.e. what must happen when this Observable is unsubscribed from
    return () => unsubscribeFromStore();
  });
}

/**
 * Svelte Writable store that doubles as an RxJS BehaviorSubject.
 * This snippet is derived from code Ben Lesh himself shared.
 * See https://github.com/ReactiveX/rxjs/issues/4740
 */
export class RxWritable<T> extends BehaviorSubject<T> {
  set(value: T) {
    super.next(value);
  }
  
  update(callback: (value: T) => T) {
    super.next(callback(super.value));
  }
}

/**
 * Create an RxJS observable from the native ResizeObserver.
 * @param element The element to observe.
 * @returns An observable.
 */
export function heightFromResizeObserver(element: HTMLElement): Observable<number>  {
  return new Observable((subscriber) => {
    const ro = new ResizeObserver(() => subscriber.next(element.offsetHeight));
    ro.observe(element);
    return function unsubscribe() {
      ro.unobserve(element);
    };
  });
}

/**
 * Create an RxJS observable from the native ResizeObserver.
 * @param element The element to observe.
 * @returns An observable.
 */
export function widthFromResizeObserver(element: HTMLElement): Observable<number> {
  return new Observable((subscriber) => {
    const ro = new ResizeObserver(() => subscriber.next(element.offsetWidth));
    ro.observe(element);
    return function unsubscribe() {
      ro.unobserve(element);
    };
  });
}

export const nav$: Observable<Navigation | null> = fromStore(navigating).pipe(filter((nav) => !!nav));

export const page$: Observable<Page> = fromStore(page);

export const pageRouteParams$: Observable<Record<string, string>> = page$.pipe(
  map((page) => page.params)
);

export const pageSearchParams$: Observable<Record<string, string>> = page$.pipe(
  map((page) => Object.fromEntries(page.url.searchParams.entries()))
);

/**
 * Poll an action function at a given interval.
 * @param ms Time to wait between each poll.
 * @param action Function to poll. Typically an async function like fetch.
 * @param followup Function to determine the next delay. If negative, the observable
 * completes. If positive, it will be the delay until the next poll. If `undefined`, `ms`
 * is used and polls while subscribed.
 * @returns An observable that while subscribed to will poll the action function.
 */
export function poller<DataType>(
  ms:number,
  action: (i:number) => Observable<DataType> | Promise<DataType>,
  followup?: (data:DataType) => number,
) {
  return new Observable<DataType>((subscriber) => {
    let setTimer = true;
    let timer: ReturnType<typeof setTimeout>;
    let count = 0;
    const poll = async () => {
      const i = count++;
      let awaited = action(i);
      if (awaited instanceof Observable) {
        awaited = lastValueFrom(awaited);
      }
      let data:DataType;
      try {
        data = await awaited;
      } catch (error) {
        subscriber.error(error);
        return;
      }
      subscriber.next(data);
      let delay = ms;
      try {
        if (followup) delay = followup(data);
      } catch (error) {
        console.warn('Poller followup function error', error)
      }
      if (delay < 0) {
        subscriber.complete();
        return;
      } else if (setTimer) {
        timer = setTimeout(poll, ms);
      }
    };
    poll(); // Start polling
    return () => {
      setTimer = false;
      clearTimeout(timer);
    };
  });
}

/**
 * Creates an observable that replays events at a given speed.
 * @param events Objects with a timestamp.
 * @param getTime A function that extracts the timestamp from an event.
 * @param speed The speed at which to replay the events. 2 means twice as fast.
 * @param delay An initial delay before starting the replay.
 * @returns A stream of events replayed at the given speed.
 */
export function replay<T>(events:T[], getTime: (event:T) => number, speed = 1, delay = 0, scheduleLog?: (tmin:number, event?:T) => void) {
  const thenFirst = getTime(events[0]);
  const intervals = events.map(event => (getTime(event) - thenFirst) / speed + delay);

  let index = 0;
  let timer:ReturnType<typeof setTimeout>;
  const nowFirst = Date.now();
  const scheduleNext = (subscriber:Subscriber<T>) => {
    const i = index++;
    const elapsed = Date.now() - nowFirst;
    const ms = Math.max(0, intervals[i] - elapsed);
    scheduleLog?.(ms, events[i])
    timer = setTimeout(() => {
      subscriber.next(events[i]);
      if (index < intervals.length) scheduleNext(subscriber);
    }, ms)
  }
  return new Observable<T>(subscriber => {
    scheduleNext(subscriber);
    return () => clearTimeout(timer);
  })
}
